import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import { makeStyles, useTheme } from '@mui/styles'
import { useMediaQuery } from '@mui/material'
import ThemeInterface from '@/theme-interface'
import lang from '@/lang'
import { brandText } from '@/utils/brand-typography'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { switchAvvState, switchAgbState } from '@/redux/ducks/onboarding/main-v2'
import { Checkbox } from '@/modules/base/components/inputs'
import { SquareIconButton } from '@/modules/base/components/buttons'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import avvPdf from '@assets/downloads/avv-orgamax-102024.pdf'
import AvvInfoSheet from './avv-info-sheet'

// minimum touch target on a phone
const TAP_SIZE = 44

interface AvvConsentProps {
  setInputsValid: (isValid: boolean) => void
  // "Weiter" was clicked: a missing consent is named below the row
  showError?: boolean
  dataQsId: string
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    paddingTop: 24,
    borderTop: `1px solid ${theme.palette.brand.divider}`,
  },
  // the design system checkbox: 18 px, radius 4, ticked in the brand colour; 12 px to the text
  checkbox: {
    '& input + div': {
      width: 18,
      minWidth: 18,
      height: 18,
      borderRadius: 4,
      border: `1px solid ${theme.palette.brand.inputBorder}`,
      background: theme.palette.background.paper,
    },
    '& input:checked + div': {
      borderColor: theme.palette.brand.main,
      background: theme.palette.brand.main,
    },
  },
  checkboxError: {
    '& input:not(:checked) + div': {
      borderColor: theme.palette.brand.error,
    },
  },
  label: {
    paddingLeft: 12,
    ...brandText(theme, 'small'),
    letterSpacing: 0,
    color: theme.palette.brand.text.secondary,
    '& a': {
      color: theme.palette.brand.main,
      textDecoration: 'underline',
      cursor: 'pointer',
    },
  },
  // the info icon follows the text directly and wraps with it
  info: {
    display: 'inline-flex',
    verticalAlign: 'middle',
    marginLeft: 4,
  },
  infoButton: {
    '& svg': {
      color: theme.palette.brand.text.secondary,
    },
    [theme.breakpoints.down(768)]: {
      '& button': {
        minWidth: TAP_SIZE,
        minHeight: TAP_SIZE,
      },
    },
  },
  error: {
    margin: '8px 0 0',
    color: theme.palette.brand.error,
    ...brandText(theme, 'tiny'),
  },
  // the popover in the brand type: Poppins, radius and shadow like the cards of the new design
  popover: {
    '& > div': {
      borderRadius: theme.borderRadiusExtraLarge,
      boxShadow: '0px 4px 16px 0px #15233F29',
      overflow: 'hidden',
    },
  },
  popoverContent: {
    fontFamily: theme.brandFontFamilies.body,
    color: theme.palette.brand.text.primary,
    '& b': {
      fontWeight: theme.fontWeights.bold,
    },
  },
  popoverTitle: {
    margin: '0 0 8px',
    ...brandText(theme, 'regular', 'semibold'),
    color: theme.palette.brand.text.heading,
  },
  popoverText: {
    margin: '0 0 8px',
    ...brandText(theme, 'small'),
    '&:last-child': {
      marginBottom: 0,
    },
  },
}))

const AvvConsent = ({ setInputsValid, showError = false, dataQsId }: AvvConsentProps) => {
  const classes = useStyles()
  const theme = useTheme<ThemeInterface>()
  const isMobile = useMediaQuery(theme.breakpoints.down(768))
  const [isInfoSheetOpen, setIsInfoSheetOpen] = useState(false)
  const dispatch = useAppDispatch()
  const { acceptAvv, acceptAgb } = useAppSelector(state => state.onboarding.mainV2)
  const hasError = showError && !acceptAvv

  useEffect(() => {
    setInputsValid(acceptAvv && acceptAgb)
  }, [acceptAvv, acceptAgb])

  // one checkbox accepts both AVV and AGB, exactly like AcceptAvvAndAgbForm
  const handleChange = () => {
    dispatch(switchAvvState())
    dispatch(switchAgbState())
  }

  const onAvvLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation()
    event.preventDefault()
    window.open(avvPdf)
  }

  const avvLink = (
    <a href={avvPdf} onClick={onAvvLinkClick} data-qs-id={`${dataQsId}-link`}>{lang.onboardingV2.avv.texts.avv}</a>
  )

  const infoButton = (onClick?: () => void) => (
    <SquareIconButton
      variant='icon'
      icon={InfoOutlinedIcon}
      customIconSize='24px'
      ariaLabel={lang.registration.avvInfo.buttonLabel}
      buttonWrapperClass={classes.infoButton}
      onClick={onClick}
      dataQsId={`${dataQsId}-info-button`}
    />
  )

  // desktop: opens on hover and keyboard focus; phone: a bottom sheet on tap
  const info = isMobile
    ? infoButton(() => setIsInfoSheetOpen(true))
    : (
      <RichTooltip
        placement='top'
        width={360}
        enterDelay={0}
        enterTouchDelay={0}
        styles={{ tooltip: classes.popover }}
        content={(
          <div className={classes.popoverContent} data-qs-id={`${dataQsId}-info-popover`}>
            <h3 className={classes.popoverTitle}>{lang.registration.avvInfo.title}</h3>
            <p className={classes.popoverText}>{lang.registration.avvInfo.text}</p>
            <p className={classes.popoverText}>{lang.registration.avvInfo.hint}</p>
          </div>
        )}>
        <span>{infoButton()}</span>
      </RichTooltip>
    )

  return (
    <div className={classes.root}>
      <Checkbox
        checked={acceptAvv}
        onChange={handleChange}
        name='acceptAvv'
        customClass={clsx(classes.checkbox, hasError && classes.checkboxError)}
        dataQsId={`${dataQsId}-checkbox`}
      >
        <span className={classes.label}>
          {lang.onboardingV2.avv.texts.avvText(avvLink)}
          {/* inside the label, so it wraps with the text - a click on it must not tick the checkbox */}
          <span className={classes.info} onClick={event => event.stopPropagation()}>{info}</span>
        </span>
      </Checkbox>
      {hasError && <p className={classes.error} role='alert' data-qs-id={`${dataQsId}-error`}>{lang.registration.avvInfo.required}</p>}
      {isMobile && (
        <AvvInfoSheet
          open={isInfoSheetOpen}
          onOpen={() => setIsInfoSheetOpen(true)}
          onClose={() => setIsInfoSheetOpen(false)}
          dataQsId={`${dataQsId}-info-sheet`}
        />
      )}
    </div>
  )
}

export default AvvConsent
